import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Grid, Typography, Card, Button, TextField } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import axios from "../../../api";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import {
  ArrowBack,
  CancelOutlined,
  CheckBoxOutlined,
  HomeOutlined,
  LocalShippingOutlined,
  SettingsOutlined,
  Send,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { generateInvoicePDF } from "./generateInvoicePdf";
import { DownloadIcon } from "lucide-react";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface Order {
  order_id: number;
  status: string;
  order_number: string;
  order_date: string | Date;
  order_status: string;
  spare_part_name: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  address_type: string;
  state_name: string;
  city_name: string;
  region_name: string;
  sku: string;
  quantity: number;
  unit_price: number;
  sub_total: number;
  gst_amount: number;
  total_price: number;
  billing_name: string;
  billing_address: string;
  billing_city: string;
  billing_phone: string;
  billing_email: string;
  cancelled_at: string | Date;
  delivered_at: string | Date;
  shipped_at: string | Date;
  shipping_address: string;
  shipping_name: string;
  grand_total: number;
}

interface Stage {
  key: string;
  label: string;
  icon: React.ReactElement;
  date: string | Date;
  isDone: boolean;
}

// ---------- EXTRACTED COMPONENTS ----------

interface TotalCellProps {
  value: number;
}

const formatCurrency = (value?: number | string) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) {
    return `$${value}`;
  }
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const TotalCell: React.FC<TotalCellProps> = ({ value }) => (
  <Typography variant="subtitle2" fontWeight="bold">
    {formatCurrency(value)}
  </Typography>
);

interface StageItemProps {
  stage: Stage;
  color: string;
  isCancelled: boolean;
}

const StageItem: React.FC<StageItemProps> = ({ stage, color, isCancelled }) => (
  <Grid item xs={12}>
    <Box display="flex" alignItems="center" gap={1}>
      {React.cloneElement(stage.icon as React.ReactElement<any>, {
        sx: { color, fontSize: 22 },
      })}
      <Typography
        fontWeight="bold"
        sx={{
          fontSize: "14px",
          color,
          textDecoration: isCancelled ? "line-through" : "none",
        }}
      >
        {stage.label}
      </Typography>
    </Box>

    <Typography sx={{ ml: 4, fontSize: "12px", color }}>
      {stage.date
        ? dayjs(stage.date).format("MMM DD, YYYY [at] hh:mm A")
        : "Pending"}
    </Typography>
  </Grid>
);

// ---------- HELPER FUNCTIONS ----------

const getStatusColor = (status: string): string => {
  switch (status) {
    case "Delivered":
      return "#e1f2f2";
    case "Shipped":
      return "#1976d2";
    case "Pending":
      return "#fdf5e1";
    case "Cancelled":
      return "#fceddd";
    default:
      return "#fceddd";
  }
};

const getStageColor = (isCancelled: boolean, isDone: boolean): string => {
  if (isCancelled) return "red";
  if (isDone) return "#008080";
  return "gray";
};

const ViewOrder = () => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`/spare-order/${id}`);
      setOrder(res?.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line
  }, [id]);

  const handleStatusChange = async (status: string) => {
    if (!order) return;

    try {
      setLoading(true);
      const res = await axios.put(`/spare-order/${order.order_id}/status`, {
        status,
      });

      if (res.data.success) {
        fetchSuccess(`Order marked as ${status}`);
        await fetchOrder();
      } else {
        fetchError(res.data.message || "Failed to update order status");
      }
    } catch (err) {
      console.error("Status update error:", err);
      fetchError("Error updating order status");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!note.trim()) {
      fetchError("Please enter a note");
      return;
    }

    try {
      setNoteLoading(true);
      const res = await axios.post(`/spare-order/${order?.order_id}/notes`, {
        note: note.trim(),
        addedBy: "Admin",
      });

      if (res.data.success) {
        fetchSuccess("Note added successfully");
        setNote("");
        await fetchOrder();
      } else {
        fetchError(res.data.message || "Failed to add note");
      }
    } catch (err) {
      console.error("Add note error:", err);
      fetchError("Error adding note");
    } finally {
      setNoteLoading(false);
    }
  };

  // Render function for table column
  const renderTotalCell = (value: number) => {
    return <TotalCell value={value} />;
  };

  const columns1: MUIDataTableColumn[] = React.useMemo(
    () => [
      {
        name: "product",
        label: "Product",
      },
      { name: "sku", label: "SKU" },
      { name: "qty", label: "Quantity" },
      { name: "price", label: "Amount" },
      {
        name: "total",
        label: "Total",
        options: {
          customBodyRender: renderTotalCell,
        },
      },
    ],
    // eslint-disable-next-line
    []
  );

  const tableOptions: MUIDataTableOptions = {
    selectableRows: "none",
    selectableRowsHeader: false,
    elevation: 0,
    tableBodyHeight: "auto",
    responsive: "standard",
  };

  if (!order) return <Typography>Loading...</Typography>;

  const bgColor = getStatusColor(order?.status);

  const stages: Stage[] = [
    {
      key: "created",
      label: "Order Created",
      icon: <CheckBoxOutlined />,
      date: order?.order_date,
      isDone: !!order?.order_date,
    },
    {
      key: "pending",
      label: "Pending",
      icon: <SettingsOutlined />,
      date: order?.order_date,
      isDone: ["Pending", "Shipped", "Delivered"].includes(order?.order_status),
    },
    {
      key: "shipped",
      label: "Shipped",
      icon: <LocalShippingOutlined />,
      date: order?.shipped_at,
      isDone: ["Shipped", "Delivered"].includes(order?.order_status),
    },
    {
      key: "delivered",
      label: "Delivered",
      icon: <HomeOutlined />,
      date: order?.delivered_at,
      isDone: order?.order_status === "Delivered",
    },
  ];

  const isCancelled = order?.order_status === "Cancelled";

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Box sx={{ p: 3 }}>
        <ArrowBack onClick={() => navigate(-1)} sx={{ fontSize: 28, mr: 1 }} />

        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {order.order_number}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Created on{" "}
              {dayjs(order.order_date).format("MMM DD, YYYY [at] hh:mm A")}
            </Typography>
          </Box>
          {order?.order_status !== "Delivered" && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {order?.order_status === "Pending" && (
                <Button
                  variant="contained"
                  startIcon={<CheckBoxOutlined />}
                  sx={{
                    position: "relative",
                    minWidth: 140,
                    ml: 2.5,
                    fontWeight: "bold",
                    textTransform: "none",
                    backgroundColor: "#1976d2!important",
                  }}
                  type="button"
                  disabled={loading}
                  onClick={() => handleStatusChange("Shipped")}
                >
                  {loading ? "Processing..." : "Shipped"}
                </Button>
              )}

              {(order?.order_status === "Pending" ||
                order?.order_status === "Shipped") && (
                <Button
                  variant="contained"
                  startIcon={<CheckBoxOutlined />}
                  sx={{
                    position: "relative",
                    minWidth: 160,
                    ml: 2.5,
                    fontWeight: "bold",
                    textTransform: "none",
                    backgroundColor: "#008080 !important",
                  }}
                  type="button"
                  disabled={loading}
                  onClick={() => handleStatusChange("Delivered")}
                >
                  {loading ? "Processing..." : "Mark As Completed"}
                </Button>
              )}

              {order?.order_status !== "Delivered" &&
                order?.order_status !== "Cancelled" && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<CancelOutlined />}
                    sx={{
                      position: "relative",
                      minWidth: 140,
                      ml: 2.5,
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                    type="button"
                    disabled={loading}
                    onClick={() => handleStatusChange("Cancelled")}
                  >
                    {loading ? "Processing..." : "Cancel Order"}
                  </Button>
                )}
            </Box>
          )}
        </Grid>

        {/* Order and Customer Information */}
        <Grid container spacing={2} mb={2} alignItems="stretch">
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: 1, flex: 1, width: "100%" }}>
              <Box sx={{ lineHeight: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                  Order Information
                </Typography>
                <Grid container>
                  <Grid item xs={4} sm={4} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Order Id</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={8} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>: {order?.order_number}</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Order Date</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={8} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>
                          :
                          {order?.order_date
                            ? dayjs(order.order_date).format("MMM DD, YYYY")
                            : ""}
                        </span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Status</span>
                      </Box>
                    </Typography>
                  </Grid>

                  <Grid item xs={8} sm={8} md={8} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>: </span>

                        <Box
                          sx={{
                            ml: 0.5,
                            px: 2,
                            py: 0.1,
                            borderRadius: "12px",
                            fontWeight: 400,
                            color: "#000",
                            backgroundColor: bgColor,
                          }}
                        >
                          {order?.order_status}
                        </Box>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Spare Part</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={8} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>: {order?.spare_part_name}</span>
                      </Box>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: 1, flex: 1, width: "100%" }}>
              <Box sx={{ lineHeight: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                  Customer Information
                </Typography>

                <Grid container>
                  <Grid item xs={4} sm={4} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Name</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={8} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>: {order?.customer_name}</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Email</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={8} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>: {order?.customer_email} </span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Phone</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={8} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>: {order?.customer_phone} </span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Address</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={8} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span className="text-break">
                          : {order?.customer_address},{order?.address_type},
                          {order?.region_name},{order?.city_name},
                          {order?.state_name}
                        </span>
                      </Box>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Products Summary and Order Timeline */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={9}>
            <Card
              elevation={0}
              sx={{
                p: 1,
                borderRadius: 3,
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            >
              <ThemeProvider theme={getMuiTheme}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    px: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Products Ordered
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    sx={{
                      minWidth: 180,
                      fontWeight: "bold",
                      textTransform: "none",
                      backgroundColor: "#1976d2 !important",
                    }}
                    onClick={() => generateInvoicePDF(order)}
                  >
                    Download Invoice
                  </Button>
                </Box>

                <MUIDataTable
                  title={""}
                  data={[
                    {
                      product: order.spare_part_name,
                      sku: order.sku || "-",
                      qty: order.quantity,
                      price: order.unit_price,
                      total: order.sub_total,
                    },
                  ]}
                  columns={columns1}
                  options={tableOptions}
                />
              </ThemeProvider>

              {/* Totals */}
              <Grid container>
                <Grid item xs={4} sm={4} md={10} sx={{ my: 1, pl: 1.8 }}>
                  <Typography variant="h6">
                    <Box display="flex" justifyContent="flex-end">
                      <span>SubTotal :</span>
                    </Box>
                  </Typography>
                </Grid>
                <Grid item xs={8} sm={8} md={2} sx={{ my: 1, pl: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                    <Box display="flex" justifyContent="flex-start">
                      <span>{formatCurrency(order?.sub_total)}</span>
                    </Box>
                  </Typography>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={4} sm={4} md={10} sx={{ my: 1, pl: 1.8 }}>
                  <Typography variant="h6">
                    <Box display="flex" justifyContent="flex-end">
                      <span>TAX :</span>
                    </Box>
                  </Typography>
                </Grid>
                <Grid item xs={8} sm={8} md={2} sx={{ my: 1, pl: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                    <Box display="flex" justifyContent="flex-start">
                      <span>{formatCurrency(order?.gst_amount)}</span>
                    </Box>
                  </Typography>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={4} sm={4} md={10} sx={{ my: 1, pl: 1.8 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "#000" }}
                  >
                    <Box display="flex" justifyContent="flex-end">
                      <span>Grand Total :</span>
                    </Box>
                  </Typography>
                </Grid>
                <Grid item xs={8} sm={8} md={2} sx={{ my: 1, pl: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "#000" }}
                  >
                    <Box display="flex" justifyContent="flex-start">
                      <span>{formatCurrency(order?.total_price)}</span>
                    </Box>
                  </Typography>
                </Grid>
              </Grid>
            </Card>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {/* Billing Address */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    mb={1}
                    sx={{ fontSize: "14px" }}
                  >
                    Billing Address
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ lineHeight: 1.8, fontSize: "14px" }}
                  >
                    {order?.billing_name || order?.customer_name} <br />
                    {order?.billing_address ||
                      `${order?.customer_address},${order?.address_type}`}{" "}
                    <br />
                    {order?.billing_city ||
                      `${order?.state_name}, ${order?.city_name},${order?.region_name}`}
                    <br />
                    {order?.billing_phone || order?.customer_phone} <br />
                    {order?.billing_email || order?.customer_email}
                  </Typography>
                </Card>
              </Grid>

              {/* Shipping Address */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    mb={1}
                    sx={{ fontSize: "14px" }}
                  >
                    Shipping Address
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ lineHeight: 1.8, fontSize: "14px" }}
                  >
                    {order?.billing_name || order?.customer_name} <br />
                    {order?.billing_address ||
                      `${order?.customer_address},${order?.address_type}`}{" "}
                    <br />
                    {order?.billing_city ||
                      `${order?.state_name}, ${order?.city_name},${order?.region_name}`}
                    <br />
                    {order?.billing_phone || order?.customer_phone} <br />
                    {order?.billing_email || order?.customer_email}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: 1, mb: 2 }}>
              <Box sx={{ lineHeight: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                  Order Timeline
                </Typography>

                <Grid container spacing={2}>
                  {stages.map((stage) => {
                    const color = getStageColor(isCancelled, stage.isDone);
                    return (
                      <StageItem
                        key={stage.key}
                        stage={stage}
                        color={color}
                        isCancelled={isCancelled}
                      />
                    );
                  })}

                  {isCancelled && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CancelOutlined sx={{ color: "red" }} />
                        <Typography
                          fontWeight="bold"
                          sx={{ fontSize: "14px", color: "red" }}
                        >
                          Cancelled
                        </Typography>
                      </Box>
                      <Typography
                        sx={{ ml: 4, fontSize: "12px", color: "red" }}
                      >
                        {order?.cancelled_at
                          ? dayjs(order.cancelled_at).format(
                              "MMM DD, YYYY [at] hh:mm A"
                            )
                          : "N/A"}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Card>

            {/* Notes Card */}
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: 1 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                mb={2}
                sx={{ fontSize: "16px" }}
              >
                Add Note
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Enter your note here..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<Send />}
                disabled={noteLoading || !note.trim()}
                onClick={handleAddNote}
                sx={{
                  fontWeight: "bold",
                  textTransform: "none",
                  backgroundColor: "#1976d2 !important",
                }}
              >
                {noteLoading ? "Adding Note..." : "Add Note"}
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
};

export default ViewOrder;
